import type { ReactNode } from "react";
import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { MobileFilterDrawer } from "./MobileFilterDrawer";
import { useI18n } from "@/lib/i18n/useI18n";

const BTN_BASE = {
  color:      "oklch(0.545 0.020 60 / 0.70)",
  background: "oklch(0.988 0.008 70 / 0.82)",
  border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
} as const;

// ─── Marketplace toolbar ────────────────────────────────────────────────────────
// Lightweight results-page toolbar: filters disclosure (mobile/tablet — filters
// live in the always-visible left rail on xl+) and "new search". Reordering now
// lives in the selection tray, so there is no view-mode or edit-mode toggle here.
//
// Marketplace Filter Polish V2.1, task 4: the drawer now has a real bottom
// action bar — "Limpiar filtros" (only when something is active) clears the
// marketplace filters ONLY (never the search query/dates/travelers, which
// live entirely outside this component's state), and "Ver resultados" just
// closes the drawer — it never re-submits or regenerates anything.
export function MarketplaceToolbar({
  onNewSearch,
  onClearFilters,
  filterBar,
  filterNote,
  activeFilterCount,
}: {
  onNewSearch:        () => void;
  onClearFilters:     () => void;
  filterBar?:         ReactNode;
  filterNote?:        ReactNode;
  activeFilterCount:  number;
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  const { t } = useI18n();

  return (
    <div className="mb-4" role="toolbar" aria-label={t("marketplace.toolbar.ariaLabel")}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter toggle — hidden on xl (filters always visible in left rail) */}
          {filterBar && (
            <span className="xl:hidden">
              <button
                type="button"
                onClick={() => setFilterOpen(v => !v)}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-2.5 sm:py-1.5 rounded-lg transition-all duration-150 active:scale-95"
                style={activeFilterCount > 0 ? {
                  color:      "#5A5A5A",
                  background: "#E8194B1A",
                  border:     "1px solid #E8194B73",
                } : BTN_BASE}
                aria-expanded={filterOpen}
                aria-label={filterOpen ? t("marketplace.toolbar.filtersCloseAria") : t("marketplace.toolbar.filtersOpenAria")}
              >
                <SlidersHorizontal size={12} />
                {t("marketplace.toolbar.filters")}
                {activeFilterCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-black leading-none"
                    style={{ background: "#111111", color: "#FFFFFF" }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </span>
          )}
        </div>

        {/* Right: nueva búsqueda — label hidden on mobile to prevent row wrap */}
        <button
          type="button"
          onClick={onNewSearch}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-2.5 sm:py-1.5 rounded-lg transition-all duration-150 active:scale-95"
          style={BTN_BASE}
          aria-label={t("marketplace.toolbar.newSearch")}
          title={t("marketplace.toolbar.newSearch")}
        >
          ←<span className="hidden sm:inline"> {t("marketplace.toolbar.newSearch")}</span>
        </button>
      </div>

      {/* Filter drawer — compact bottom sheet, hidden on xl (always in left rail) */}
      {filterBar && (
        <MobileFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          title={t("marketplace.toolbar.drawerTitle")}
          subtitle={t("marketplace.toolbar.drawerSubtitle")}
          footer={
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="flex-1 h-10 rounded-lg text-[12px] font-semibold transition-opacity hover:opacity-70"
                  style={{
                    color:      "#5A5A5A",
                    background: "#5A5A5A14",
                    border:     "1px solid #5A5A5A40",
                  }}
                >
                  {t("marketplace.toolbar.clearFilters")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 h-10 rounded-lg text-[12px] font-bold transition-opacity hover:opacity-90"
                style={{ color: "oklch(0.998 0.003 72)", background: "oklch(0.120 0.040 258)" }}
              >
                {t("marketplace.toolbar.viewResults")}
              </button>
            </div>
          }
        >
          {filterBar}
          {filterNote && (
            <p className="mt-3 text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
              {filterNote}
            </p>
          )}
        </MobileFilterDrawer>
      )}
    </div>
  );
}
